import React, { useState, useEffect } from 'react';
import { adminTestApi } from '../../services/adminApi';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/ConfirmDialog';

interface Test {
  id: number;
  title: string;
  description: string;
  type: 'LOGIC' | 'CASE_STUDY';
  time_limit: number | null;
  is_active: boolean;
  questions?: Question[];
}

interface Question {
  id: number;
  question_type: 'MULTIPLE_CHOICE' | 'SHORT_ANSWER' | 'ESSAY';
  question: string;
  options: string[] | null;
  answer: string | null;
  score: number;
  order_num: number;
}

interface Props {
  onBack: () => void;
}

const TestManagement: React.FC<Props> = ({ onBack }) => {
  const { toast } = useToast();
  const confirmDialog = useConfirm();
  const [tests, setTests] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [editingTest, setEditingTest] = useState<Partial<Test> | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Partial<Question> | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);

  useEffect(() => {
    loadTests();
  }, []);

  const loadTests = async () => {
    try {
      const data = await adminTestApi.getTests();
      setTests(data);
    } catch (err) {
      console.error('시험 목록 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTestDetail = async (testId: number) => {
    try {
      const data = await adminTestApi.getTest(testId);
      setSelectedTest(data);
    } catch (err) {
      console.error('시험 상세 로드 실패:', err);
    }
  };

  const handleSaveTest = async () => {
    if (!editingTest) return;
    try {
      if (editingTest.id) {
        await adminTestApi.updateTest(editingTest.id, editingTest);
      } else {
        await adminTestApi.createTest(editingTest);
      }
      loadTests();
      setShowTestModal(false);
      if (editingTest.id && selectedTest?.id === editingTest.id) {
        loadTestDetail(editingTest.id);
      }
      setEditingTest(null);
    } catch (err) {
      toast.error('저장에 실패했습니다.');
    }
  };

  const handleDeleteTest = async (testId: number) => {
    const ok = await confirmDialog({
      title: '시험을 삭제하시겠습니까?',
      message: '모든 문제도 함께 삭제됩니다.',
      confirmText: '삭제',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await adminTestApi.deleteTest(testId);
      loadTests();
      if (selectedTest?.id === testId) {
        setSelectedTest(null);
      }
    } catch (err) {
      toast.error('삭제에 실패했습니다.');
    }
  };

  const handleSaveQuestion = async () => {
    if (!editingQuestion || !selectedTest) return;
    try {
      if (editingQuestion.id) {
        await adminTestApi.updateQuestion(editingQuestion.id, editingQuestion);
      } else {
        await adminTestApi.addQuestion(selectedTest.id, editingQuestion);
      }
      loadTestDetail(selectedTest.id);
      setShowQuestionModal(false);
      setEditingQuestion(null);
    } catch (err) {
      toast.error('저장에 실패했습니다.');
    }
  };

  const handleDeleteQuestion = async (questionId: number) => {
    const ok = await confirmDialog({
      title: '문제를 삭제하시겠습니까?',
      confirmText: '삭제',
      variant: 'danger',
    });
    if (!ok) return;
    if (!selectedTest) return;
    try {
      await adminTestApi.deleteQuestion(questionId);
      loadTestDetail(selectedTest.id);
    } catch (err) {
      toast.error('삭제에 실패했습니다.');
    }
  };

  const questionTypeLabels: Record<string, string> = {
    MULTIPLE_CHOICE: '객관식',
    SHORT_ANSWER: '단답형',
    ESSAY: '서술형',
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-slate-800">시험 관리</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* 시험 목록 */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-semibold text-slate-800">시험 목록</h2>
                <button
                  onClick={() => { setEditingTest({ type: 'LOGIC', is_active: true }); setShowTestModal(true); }}
                  className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  + 새 시험
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {loading ? (
                  <div className="p-4 text-center text-slate-400">로딩 중...</div>
                ) : tests.length === 0 ? (
                  <div className="p-4 text-center text-slate-400">등록된 시험이 없습니다</div>
                ) : (
                  tests.map((test) => (
                    <div
                      key={test.id}
                      onClick={() => loadTestDetail(test.id)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 ${selectedTest?.id === test.id ? 'bg-indigo-50' : ''}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-medium text-slate-800">{test.title}</h3>
                          <p className="text-xs text-slate-500 mt-1">
                            {test.type === 'LOGIC' ? '논리력 평가' : '케이스 스터디'}
                            {test.time_limit && ` · ${test.time_limit}분`}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${test.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {test.is_active ? '활성' : '비활성'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* 시험 상세 / 문제 목록 */}
          <div className="md:col-span-2">
            {selectedTest ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-semibold text-slate-800">{selectedTest.title}</h2>
                      <p className="text-sm text-slate-500 mt-1">{selectedTest.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingTest(selectedTest); setShowTestModal(true); }}
                        className="text-xs px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteTest(selectedTest.id)}
                        className="text-xs px-3 py-1.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-medium text-slate-700">문제 목록 ({selectedTest.questions?.length || 0}개)</h3>
                  <button
                    onClick={() => { setEditingQuestion({ question_type: 'MULTIPLE_CHOICE', score: 10, options: ['', '', '', ''] }); setShowQuestionModal(true); }}
                    className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    + 문제 추가
                  </button>
                </div>

                <div className="divide-y divide-slate-100">
                  {!selectedTest.questions || selectedTest.questions.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">등록된 문제가 없습니다</div>
                  ) : (
                    selectedTest.questions.map((q, idx) => (
                      <div key={q.id} className="p-4">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-medium">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                                {questionTypeLabels[q.question_type]}
                              </span>
                              <span className="text-xs text-slate-400">{q.score}점</span>
                            </div>
                            <p className="text-sm text-slate-800 whitespace-pre-wrap line-clamp-2">{q.question}</p>
                            {q.options && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {q.options.map((opt, i) => (
                                  <span key={i} className={`text-xs px-2 py-0.5 rounded ${q.answer === opt ? 'bg-green-100 text-green-700' : 'bg-slate-50 text-slate-500'}`}>
                                    {i + 1}. {opt}
                                  </span>
                                ))}
                              </div>
                            )}
                            {q.question_type === 'SHORT_ANSWER' && q.answer && (
                              <p className="mt-1 text-xs text-green-600">정답: {q.answer}</p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => { setEditingQuestion(q); setShowQuestionModal(true); }}
                              className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
                            >
                              수정
                            </button>
                            <button
                              onClick={() => handleDeleteQuestion(q.id)}
                              className="text-xs px-2 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400">
                시험을 선택해주세요
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 시험 생성/수정 모달 */}
      {showTestModal && editingTest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {editingTest.id ? '시험 수정' : '새 시험 등록'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">시험 제목</label>
                <input
                  type="text"
                  value={editingTest.title || ''}
                  onChange={(e) => setEditingTest({ ...editingTest, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="예: 논리력 평가"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
                <textarea
                  value={editingTest.description || ''}
                  onChange={(e) => setEditingTest({ ...editingTest, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="시험 안내 문구"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">유형</label>
                  <select
                    value={editingTest.type}
                    onChange={(e) => setEditingTest({ ...editingTest, type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="LOGIC">논리력 평가</option>
                    <option value="CASE_STUDY">케이스 스터디</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">제한시간 (분)</label>
                  <input
                    type="number"
                    value={editingTest.time_limit || ''}
                    onChange={(e) => setEditingTest({ ...editingTest, time_limit: e.target.value ? parseInt(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="미입력 시 무제한"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingTest.is_active}
                  onChange={(e) => setEditingTest({ ...editingTest, is_active: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <label htmlFor="is_active" className="text-sm text-slate-700">활성화</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowTestModal(false); setEditingTest(null); }}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={handleSaveTest}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 문제 생성/수정 모달 */}
      {showQuestionModal && editingQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-y-auto py-8">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {editingQuestion.id ? '문제 수정' : '새 문제 추가'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">문제 유형</label>
                  <select
                    value={editingQuestion.question_type}
                    onChange={(e) => {
                      const type = e.target.value as any;
                      setEditingQuestion({
                        ...editingQuestion,
                        question_type: type,
                        options: type === 'MULTIPLE_CHOICE' ? ['', '', '', ''] : null,
                        answer: type === 'ESSAY' ? null : editingQuestion.answer,
                      });
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="MULTIPLE_CHOICE">객관식</option>
                    <option value="SHORT_ANSWER">단답형</option>
                    <option value="ESSAY">서술형</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">배점</label>
                  <input
                    type="number"
                    value={editingQuestion.score || ''}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, score: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">문제</label>
                <textarea
                  value={editingQuestion.question || ''}
                  onChange={(e) => setEditingQuestion({ ...editingQuestion, question: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="문제 내용을 입력하세요"
                />
              </div>

              {editingQuestion.question_type === 'MULTIPLE_CHOICE' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">보기 (정답 선택)</label>
                  <div className="space-y-2">
                    {(editingQuestion.options || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={editingQuestion.answer === opt && opt !== ''}
                          onChange={() => setEditingQuestion({ ...editingQuestion, answer: opt })}
                          className="w-4 h-4 text-indigo-600"
                        />
                        <span className="text-sm text-slate-500 w-6">{idx + 1}.</span>
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => {
                            const newOptions = [...(editingQuestion.options || [])];
                            newOptions[idx] = e.target.value;
                            setEditingQuestion({
                              ...editingQuestion,
                              options: newOptions,
                              answer: editingQuestion.answer === opt ? e.target.value : editingQuestion.answer,
                            });
                          }}
                          className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder={`보기 ${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editingQuestion.question_type === 'SHORT_ANSWER' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">정답</label>
                  <input
                    type="text"
                    value={editingQuestion.answer || ''}
                    onChange={(e) => setEditingQuestion({ ...editingQuestion, answer: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="정답을 입력하세요"
                  />
                </div>
              )}

              {editingQuestion.question_type === 'ESSAY' && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-500">
                  서술형 문제는 정답이 없으며, 관리자가 직접 평가합니다.
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowQuestionModal(false); setEditingQuestion(null); }}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={handleSaveQuestion}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestManagement;
